import {
  ProductMetaFieldExtraProduct,
  ReactProductState,
  ProductGroupSelectionFromSteps,
  ProductStepsListing,
  ProductGroupStepType,
} from "product";
import { FC, useEffect, useMemo, useState, useCallback } from "react";
import { StoreApi, UseBoundStore } from "zustand";
import { _Product_liquid, ProductBlocksProduct_group_selector } from "./_types";
import { ProductGroupAccordion } from "./product-group-accordion";
import { ProductGroupStepSelector } from "./product-group-step-selector";
import { ProductGroupMetafieldOptionSelector } from "./product-group-metafield-option-selector";
import { ProductGroupVariantSelector } from "./product.product_group_variant_selector";
import { ProductItemCheckbox } from "./product-item-checkbox";
import { _Product, _Variant, useGlobalProducts } from "./global-stores";
import { ProductErrorBoundary } from "./product-error-boundary";
import { getVariantDisplayTitle } from "./variant-helpers";

const DRILLING_OPTION_LINE_ITEM_TOKEN = "drilling";
const TOP_OPTION_LINE_ITEM_TOKEN = "top";
type StepExcludingOption = number | string;

const DESKTOP_COLS_CLASSES = {
  "3": "md:grid-cols-3",
  "4": "md:grid-cols-4",
  "5": "md:grid-cols-5",
  "6": "md:grid-cols-6",
};
const MOBILE_COLS_CLASSES = {
  "1": "grid-cols-1",
  "2": "grid-cols-2",
  "3": "grid-cols-3",
  "4": "grid-cols-4",
};

const getGridClasses = (desktopCols?: string, mobileCols?: string) => {
  const desktop = DESKTOP_COLS_CLASSES[desktopCols || "5"] || "md:grid-cols-5";
  const mobile = MOBILE_COLS_CLASSES[mobileCols || "2"] || "grid-cols-2";
  return `grid ${desktop} ${mobile} gap-4`;
};

const getStepLineItemLabel = (step: ProductGroupSelectionFromSteps, currentStepNumber: number) => {
  if (step.sourceType === ProductGroupStepType.METAFIELD) {
    return step.lineItemLabel || step.namespace || step.title || `Step ${currentStepNumber}`;
  }
  return step.lineItemLabel || step.title || `Step ${currentStepNumber}`;
};

const getFilteringList = (filteringVariantIds?: string | unknown) => {
  if (!filteringVariantIds) {
    return undefined;
  }

  if (typeof filteringVariantIds === "string" && JSON.parse(filteringVariantIds)) {
    const metafieldValue = JSON.parse(filteringVariantIds);

    return metafieldValue;
  }

  return undefined;
};

export const ProductGroupSelector: FC<{
  useProduct: UseBoundStore<StoreApi<ReactProductState>>;
  settings: ProductBlocksProduct_group_selector["settings"];
}> = ({ useProduct, settings }) => {
  const {
    product,
    selectedVariant,
    setSelectedVariant,
    resetSelectedConfigurableProducts,
    resetSelectedProductExtraOptions,
    setSelectedConfigurableProducts,
    removeSelectedConfigurableProduct,
    selectedConfigurableProducts,
    setSelectedProductExtraOptions,
    selectedProductExtraOptions,
    setProductGroupSteps,
  } = useProduct(
    ({
      product,
      selectedVariant,
      setSelectedConfigurableProducts,
      removeSelectedConfigurableProduct,
      selectedConfigurableProducts,
      setSelectedVariant,
      resetSelectedConfigurableProducts,
      resetSelectedProductExtraOptions,
      setSelectedProductExtraOptions,
      selectedProductExtraOptions,
      setProductGroupSteps,
    }) => ({
      product,
      selectedVariant,
      setSelectedConfigurableProducts,
      removeSelectedConfigurableProduct,
      selectedConfigurableProducts,
      setSelectedVariant,
      resetSelectedConfigurableProducts,
      resetSelectedProductExtraOptions,
      setSelectedProductExtraOptions,
      selectedProductExtraOptions,
      setProductGroupSteps,
    })
  );
  const { getAsyncProduct } = useGlobalProducts(({ getAsyncProduct }) => ({ getAsyncProduct }));
  const [selectedTopVanityProduct, setSelectedTopVanityProduct] = useState<{
    product: _Product;
    variant: _Variant;
  }>({
    product: null,
    variant: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  const totalPrice = useMemo(() => {
    return selectedConfigurableProducts.reduce((acc, singleConfigProduct) => {
      return acc + singleConfigProduct.selectedVariant.price;
    }, selectedVariant.price);
  }, [selectedVariant, selectedConfigurableProducts]);

  const stepsToExclude: StepExcludingOption[] = useMemo(() => {
    if (!selectedVariant.metafields?.custom) {
      return [];
    }

    if (
      selectedVariant.metafields.custom["product_configuration_steps_excluded"] &&
      JSON.parse(selectedVariant.metafields.custom["product_configuration_steps_excluded"] as any)
    ) {
      return JSON.parse(
        selectedVariant.metafields.custom["product_configuration_steps_excluded"] as any
      );
    }

    return [];
  }, [selectedVariant]);

  const stepsFromMetaFields = useMemo(() => {
    return [
      selectedVariant?.metafields?.custom["top_surfaces"]
        ? {
            sourceType: ProductGroupStepType.METAFIELD,
            title: "Top Surface",
            lineItemLabel: "Top Surface",
            namespace: "top_surfaces",
            required: true,
            gridColumnsDesktop: settings.grid_columns_desktop,
            gridColumnsMobile: settings.grid_columns_mobile,
            items: selectedVariant.metafields.custom[
              "top_surfaces"
            ] as ProductMetaFieldExtraProduct[],
          }
        : undefined,
      selectedVariant?.metafields?.custom["cabinet_finishes"]
        ? {
            sourceType: ProductGroupStepType.METAFIELD,
            title: "Cabinet Finish",
            lineItemLabel: "Cabinet Finish",
            namespace: "cabinet_finishes",
            required: true,
            gridColumnsDesktop: settings.grid_columns_desktop,
            gridColumnsMobile: settings.grid_columns_mobile,
            items: selectedVariant.metafields.custom[
              "cabinet_finishes"
            ] as ProductMetaFieldExtraProduct[],
          }
        : undefined,
    ].filter((s) => !!s);
  }, [
    selectedVariant?.metafields?.custom,
    settings.grid_columns_desktop,
    settings.grid_columns_mobile,
  ]);

  const createStepFromSettings = useCallback(
    (stepNumber: number) => {
      const stepKey = `product_list_step_${stepNumber}`;
      return {
        sourceType: ProductGroupStepType.SETTING,
        stepNumber,
        selectorTemplate: settings[`${stepKey}_selector_display`],
        title: settings[`${stepKey}_label`],
        lineItemLabel: settings[`${stepKey}_line_item_label`],
        productList: settings[stepKey],
        gridColumnsDesktop: settings[`${stepKey}_grid_columns_desktop`] || settings.grid_columns_desktop,
        gridColumnsMobile: settings[`${stepKey}_grid_columns_mobile`] || settings.grid_columns_mobile,
        quantityToAdd: Number(
          selectedVariant?.metafields?.custom[
            `product_configuration_step_${stepNumber}_quantity_to_add`
          ]
            ? selectedVariant.metafields.custom[
                `product_configuration_step_${stepNumber}_quantity_to_add`
              ]
            : settings[`${stepKey}_quantity_to_add`]
        ),
        displayPrices: Boolean(
          Number(
            selectedVariant?.metafields?.bathroomspace?.[
              `product_configuration_step_${stepNumber}_display_prices`
            ]
              ? selectedVariant.metafields.bathroomspace[
                  `product_configuration_step_${stepNumber}_display_prices`
                ]
              : settings[`${stepKey}_display_prices`]
          )
        ),
        filteringVariantIds: getFilteringList(
          selectedVariant?.metafields?.custom[
            `product_configuration_step_${stepNumber}_filtering_list`
          ]
        ),
        required: Boolean(
          selectedVariant?.metafields?.custom[
            `product_configuration_step_${stepNumber}_is_required`
          ]
            ? selectedVariant.metafields.custom[
                `product_configuration_step_${stepNumber}_is_required`
              ]
            : settings[`${stepKey}_is_required`] === "1"
        ),
        renderingStrategy: settings[`${stepKey}_rendering_strategy`],
        openInfoInNewTab: Boolean(settings[`${stepKey}_open_info_in_new_tab`]),
      };
    },
    [settings, selectedVariant?.metafields?.custom, selectedVariant?.metafields?.bathroomspace]
  );

  const stepsFromSettings = useMemo(() => {
    return [2, 3, 4, 5, 6, 7].map(createStepFromSettings).filter((s) => s.productList?.length > 0);
  }, [createStepFromSettings]);

  const steps = useMemo(() => {
    const initialSteps: ProductStepsListing = [...stepsFromMetaFields, ...stepsFromSettings];

    let stepsToRender = initialSteps;
    if (stepsToExclude?.length > 0) {
      stepsToRender = stepsToRender.filter((s) => {
        if (s.sourceType === ProductGroupStepType.METAFIELD) {
          return !stepsToExclude.includes(s.namespace);
        }
        return !stepsToExclude.includes(s.stepNumber);
      });
    }

    setProductGroupSteps([...stepsToRender]);
    return stepsToRender;
  }, [stepsFromMetaFields, stepsFromSettings, stepsToExclude, setProductGroupSteps]);

  // Memoize drilling and top option steps
  const { drillingOptionStep, topOptionStep } = useMemo(() => {
    const drilling = steps.find((s) =>
      s.lineItemLabel?.toLowerCase().includes(DRILLING_OPTION_LINE_ITEM_TOKEN)
    );
    const top = steps.find((s) =>
      s.lineItemLabel?.toLowerCase().includes(TOP_OPTION_LINE_ITEM_TOKEN)
    );
    return { drillingOptionStep: drilling, topOptionStep: top };
  }, [steps]);

  // Memoize filtering variant IDs for drilling options
  const drillingOptionFilteringIds = useMemo(() => {
    if (!drillingOptionStep) return undefined;
    return getFilteringList(
      selectedTopVanityProduct.variant?.metafields?.custom[`drilling_options_filtering_list`]
    );
  }, [drillingOptionStep, selectedTopVanityProduct.variant?.metafields?.custom]);

  // Check if we should hide the variant selector (step 1)
  const shouldHideVariantSelector = useMemo(() => {
    const hideVariantSelector = (settings as any).hide_variant_selector;
    if (!hideVariantSelector) {
      return false;
    }
    // Hide if there's only 1 variant configured
    return product.variants.length === 1;
  }, [settings, product.variants]);

  // Calculate total steps: if variant selector is hidden, don't count it
  const totalSteps = useMemo(() => {
    const baseSteps = steps ? steps.length : 0;
    return shouldHideVariantSelector ? baseSteps : baseSteps + 1;
  }, [steps, shouldHideVariantSelector]);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const initialize = async () => {
      try {
        // Initialize any necessary data
        if (isMounted) {
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error initializing product group:", error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    let abortController = new AbortController();

    const fetch = async () => {
      try {
        if (selectedConfigurableProducts) {
          // Get Vanity Top selected product
          const prevSelectedProduct = selectedConfigurableProducts.find(({ lineItemLabel }) => {
            return (
              lineItemLabel && lineItemLabel.toLowerCase().includes(TOP_OPTION_LINE_ITEM_TOKEN)
            );
          });

          if (!isMounted) return;

          const product = prevSelectedProduct
            ? await getAsyncProduct(prevSelectedProduct.product.handle)
            : null;

          if (product && isMounted) {
            const variant = product.variants.find(
              (v) => v.id === prevSelectedProduct.selectedVariant.id
            );
            if (variant) {
              setSelectedTopVanityProduct({ product, variant });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      }
    };

    fetch();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [selectedConfigurableProducts, steps, getAsyncProduct]);

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="mb-4 h-8 rounded bg-gray-200"></div>
        <div className="space-y-4">
          <div className="h-32 rounded bg-gray-200"></div>
          <div className="h-32 rounded bg-gray-200"></div>
          <div className="h-32 rounded bg-gray-200"></div>
        </div>
      </div>
    );
  }

  return (
    <ProductErrorBoundary>
      <div>
        {!shouldHideVariantSelector && (
          <ProductGroupAccordion
            collapsed={false}
            index={1}
            title="Select Your Style"
            rightInfo={<p className={"text-sm"}>(1/{totalSteps})</p>}
          >
            {settings.variants_selector_display === "checkboxes" && (
              <div
                className={getGridClasses(
                  settings.grid_columns_desktop,
                  settings.grid_columns_mobile
                )}
              >
                {product.variants.map((variant) => {
                  const variantId = variant.id;
                  return (
                    <ProductItemCheckbox
                      key={variantId}
                      product={product}
                      quantityToAdd={1}
                      gridColumnsDesktop={settings.grid_columns_desktop}
                      variant={variant}
                      checked={selectedVariant.id === variantId}
                      onClick={() => {
                        setSelectedVariant(variantId);
                        resetSelectedConfigurableProducts();
                        resetSelectedProductExtraOptions();
                      }}
                    />
                  );
                })}
              </div>
            )}
            {settings.variants_selector_display === "images" && (
              <ProductGroupVariantSelector
                useProduct={useProduct}
                gridColumnsDesktop={settings.grid_columns_desktop}
                gridColumnsMobile={settings.grid_columns_mobile}
              />
            )}
          </ProductGroupAccordion>
        )}

        {steps?.map((step, index) => {
          // If variant selector is hidden, steps start at 1, otherwise at 2 (variant selector is step 1)
          const stepOffset = shouldHideVariantSelector ? 1 : 2;
          const currentStepNumber = index + stepOffset;

          if (step.sourceType === ProductGroupStepType.METAFIELD) {
            return (
              <ProductErrorBoundary key={`metafield-${step.namespace}`}>
                <ProductGroupAccordion
                  collapsed={false}
                  index={currentStepNumber}
                  title={step.title}
                  rightInfo={
                    <p className={"text-sm"}>
                      ({currentStepNumber}/{totalSteps})
                    </p>
                  }
                >
                  <ProductGroupMetafieldOptionSelector
                    product={product}
                    items={step.items}
                    selection={selectedProductExtraOptions[step.namespace]}
                    namespace={step.namespace}
                    gridColumnsDesktop={step.gridColumnsDesktop}
                    gridColumnsMobile={step.gridColumnsMobile}
                    onSelect={(namespace, metaData) => {
                      setSelectedProductExtraOptions(namespace, metaData);
                    }}
                    required={step.required}
                  />
                </ProductGroupAccordion>
              </ProductErrorBoundary>
            );
          }

          const selectedProduct = selectedConfigurableProducts.find(
            ({ stepNumber }) => stepNumber === step.stepNumber
          );
          const filteringVariantIds =
            step === drillingOptionStep ? drillingOptionFilteringIds : step.filteringVariantIds;

          return (
            <ProductErrorBoundary key={`step-${step.stepNumber}`}>
              <ProductGroupStepSelector
                selectorTemplate={step.selectorTemplate as "images" | "checkboxes"}
                totalSteps={totalSteps}
                stepNumber={currentStepNumber}
                collapsed={false}
                displayPrices={step.displayPrices}
                title={step.title}
                productList={step.productList}
                selectedProduct={selectedProduct}
                quantityToAdd={step.quantityToAdd}
                filteringVariantIds={filteringVariantIds}
                gridColumnsDesktop={step.gridColumnsDesktop}
                gridColumnsMobile={step.gridColumnsMobile}
                required={step.required}
                renderingStrategy={step.renderingStrategy}
                openInfoInNewTab={step.openInfoInNewTab}
                onProductSelect={(_stepNumber, product, variant, _quantityToAdd) => {
                  setSelectedConfigurableProducts(
                    step.stepNumber,
                    product,
                    variant,
                    Number(step.quantityToAdd || 1),
                    getStepLineItemLabel(step, currentStepNumber)
                  );

                  if (step === topOptionStep && drillingOptionStep) {
                    removeSelectedConfigurableProduct(drillingOptionStep.stepNumber);
                  }
                }}
                onProductUnselect={() => {
                  removeSelectedConfigurableProduct(step.stepNumber);
                }}
              />
            </ProductErrorBoundary>
          );
        })}

        <div className={"mt-8"}>
          <div className={"flex flex-col gap-3 bg-[#DFEFFE] p-3"}>
            <ul className={"flex flex-col gap-2"}>
              <li>
                <p class={"flex justify-between gap-2 text-sm"}>
                  <span>Base price:</span>
                  <span>{window.formatMoney(selectedVariant.price)}</span>
                </p>
              </li>
              {steps
                .filter((step) => {
                  // Only display optional items when they have value.
                  if (!step.required) {
                    if (step.sourceType === ProductGroupStepType.SETTING) {
                      const selection = selectedConfigurableProducts.find(
                        (p) => p.stepNumber === step.stepNumber
                      );
                      if (selection) {
                        return true;
                      }
                    }

                    if (step.sourceType === ProductGroupStepType.METAFIELD) {
                      const metaSelection = selectedProductExtraOptions[step.namespace];
                      if (metaSelection) {
                        return true;
                      }
                    }

                    return false;
                  }

                  return true;
                })
                .map((step, index) => {
                  // If variant selector is hidden, steps start at 1, otherwise at 2 (variant selector is step 1)
                  const stepOffset = shouldHideVariantSelector ? 1 : 2;
                  const currentStepNumber = index + stepOffset;
                  let price = 0;
                  let lineText = "";
                  if (step.sourceType === ProductGroupStepType.SETTING) {
                    const selection = selectedConfigurableProducts.find(
                      (p) => p.stepNumber === step.stepNumber
                    );
                    price = selection
                      ? selection.quantityToAdd * selection.selectedVariant.price
                      : null;
                    if (selection) {
                      const variantDisplayTitle = getVariantDisplayTitle(
                        selection.product,
                        selection.selectedVariant
                      );
                      lineText =
                        variantDisplayTitle === selection.product.title
                          ? selection.product.title
                          : `${selection.product.title} - ${variantDisplayTitle}`.trim();
                    }
                  }

                  if (step.sourceType === ProductGroupStepType.METAFIELD && step.namespace) {
                    const metaSelection = selectedProductExtraOptions[step.namespace];
                    if (metaSelection) {
                      lineText = `${metaSelection?.group} - ${metaSelection?.name}`.trim();
                    } else {
                      price = null;
                      lineText = "";
                    }
                  }

                  if (step.quantityToAdd > 1) {
                    lineText += ` x${step.quantityToAdd}`;
                  }

                  return (
                    <li key={`${index}-selection-${step.lineItemLabel || "step"}`}>
                      <p className={"flex justify-between gap-2 text-xs font-semibold"}>
                        <span className={""}>{getStepLineItemLabel(step, currentStepNumber)}:</span>
                        {!lineText && (
                          <span>
                            {step.required ? (
                              <span className={"text-xs text-red-700"}>required</span>
                            ) : (
                              ""
                            )}
                          </span>
                        )}
                      </p>
                      <p class={"flex items-center justify-between gap-2 text-sm"}>
                        <span>{lineText}</span>
                        {price !== null && (
                          <span class={"flex-shrink-0"}>
                            {price > 0 ? <>+ {window.formatMoney(price)}</> : "FREE"}
                          </span>
                        )}
                      </p>
                    </li>
                  );
                })}
            </ul>
          </div>
          <div className={"flex items-center justify-between bg-[#FBF8F7] p-3"}>
            <p>Your Selection Total</p>
            <p>{window.formatMoney(totalPrice)}</p>
          </div>
        </div>
      </div>
    </ProductErrorBoundary>
  );
};
